import { properties, fmtCurrency, propertyMetrics } from "@/data/content";
import { Bed, Bath, Car, Square, TrendingUp, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

const INITIAL_COUNT = 6;

export default function Properties() {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? properties : properties.slice(0, INITIAL_COUNT);
  const remaining = properties.length - INITIAL_COUNT;

  return (
    <section id="properties" data-testid="properties-section" className="bg-[#0E1612]">
      <div className={`max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32`}>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <p className="overline mb-4">Recent Acquisitions</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight max-w-2xl">
              Properties secured for <span className="italic text-[#36C281]">clients</span>.
            </h2>
          </div>
          <p className="text-neutral-400 text-sm max-w-sm">
            Every deal chosen for long-term fundamentals — and tracked with annualised, compounded returns.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {visible.map((p, idx) => {
            const { growth, growthPct, cagr } = propertyMetrics(p);
            return (
              <article
                key={idx}
                data-testid={`property-card-${idx}`}
                className="group bg-[#131C18] border border-neutral-800 hover:border-[#36C281]/60 transition-colors duration-300"
              >
                <div className="relative overflow-hidden h-56">
                  <img
                    src={p.image}
                    alt={p.location}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  {p.yield != null && (
                    <div className="absolute top-3 left-3 bg-[#36C281] text-[#07120B] text-[10px] font-semibold px-2.5 py-1">
                      {p.yield}% yield
                    </div>
                  )}
                  <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-[#EFE9D4] text-[10px] px-2.5 py-1">
                    {p.purchaseLabel}
                  </div>
                  {p.dealType && (
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex items-center justify-center">
                      <div className="border border-[#36C281] text-[#36C281] px-6 py-3 font-semibold text-center">
                        <div className="text-sm tracking-[0.4em] uppercase">{p.dealType}</div>
                        {p.feature && (
                          <div className="text-[10px] tracking-[0.3em] uppercase text-[#36C281]/80 mt-1.5">{p.feature}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <p className="text-[9px] tracking-[0.22em] uppercase text-[#36C281]/80">{p.type || "House"}</p>
                    {p.feature && (
                      <span className="text-[9px] tracking-[0.18em] uppercase border border-[#36C281]/40 text-[#36C281] px-1.5 py-0.5">
                        {p.feature}
                      </span>
                    )}
                    {p.renovation && (
                      <span className="text-[9px] tracking-[0.18em] uppercase border border-[#36C281]/40 text-[#36C281] px-1.5 py-0.5">
                        Reno +{fmtCurrency(p.renovation)}
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif-display text-lg mb-4 leading-tight">{p.location}</h3>

                  {/* Purchase / Current / Growth */}
                  <div className="grid grid-cols-3 gap-3 border-t border-neutral-800 pt-4">
                    <div>
                      <div className="text-[9px] tracking-[0.18em] uppercase text-neutral-500">Purchase</div>
                      <div className="mt-1 text-sm text-neutral-200">{fmtCurrency(p.purchase)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] tracking-[0.18em] uppercase text-neutral-500">Current</div>
                      <div className="mt-1 text-sm text-neutral-200">{fmtCurrency(p.current)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] tracking-[0.18em] uppercase text-neutral-500">Growth</div>
                      <div className="mt-1 text-sm text-[#36C281]">+{fmtCurrency(growth)}</div>
                      <div className="text-[10px] text-[#36C281]/80">+{growthPct.toFixed(1)}%</div>
                    </div>
                  </div>

                  {/* Compounded annual growth — hero metric */}
                  <div
                    data-testid={`property-cagr-${idx}`}
                    className="mt-4 border border-[#36C281]/30 bg-[#36C281]/5 px-3 py-3 flex items-center justify-between"
                  >
                    <div>
                      <div className="text-[9px] tracking-[0.22em] uppercase text-[#36C281]/90 flex items-center gap-1.5">
                        <TrendingUp size={10} /> Compounded p.a.
                      </div>
                    </div>
                    <div className="font-serif-display text-2xl text-[#36C281]">
                      {cagr.toFixed(1)}%
                    </div>
                  </div>

                  {/* Configuration */}
                  <div className="mt-4 flex items-center gap-3 text-[11px] text-neutral-500 flex-wrap">
                    <span className="flex items-center gap-1"><Bed size={12} /> {p.beds}</span>
                    <span className="flex items-center gap-1"><Bath size={12} /> {p.baths}</span>
                    <span className="flex items-center gap-1"><Car size={12} /> {p.cars}</span>
                    {p.land && (
                      <span className="flex items-center gap-1"><Square size={12} /> {p.land}</span>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {remaining > 0 && (
          <div className="mt-12 flex justify-center" data-testid="properties-toggle-wrap">
            <button
              type="button"
              data-testid="properties-toggle-btn"
              onClick={() => setExpanded((v) => !v)}
              className="btn-outline"
            >
              {expanded ? (
                <>Show less <ChevronUp size={16} /></>
              ) : (
                <>View all {properties.length} acquisitions <ChevronDown size={16} /></>
              )}
            </button>
          </div>
        )}

        {/* Disclaimer */}
        <div
          data-testid="properties-disclaimer"
          className="mt-16 border-t border-neutral-800 pt-10 max-w-4xl"
        >
          <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-3">
            Important Information
          </p>
          <p className="text-xs text-neutral-500 leading-relaxed">
            All figures shown above are <span className="text-neutral-300">indicative only</span> and reflect estimated current market values as at <span className="text-neutral-300">June 2026</span>. Current valuations are derived from independent market data, comparable sales and desktop assessments — they are not formal bank or registered valuations and are subject to change with market conditions. Compounded annual growth rate (CAGR) figures normalise as hold periods lengthen and may fluctuate materially for properties held less than 12 months. Past performance is not a guarantee of future returns. The information on this page is provided for illustrative purposes only and does not constitute financial, investment, tax or legal advice. Investors should obtain their own professional advice before making any property investment decision. Rob Power and iScout Property accept no liability for decisions made in reliance on this information.
          </p>
        </div>
      </div>
    </section>
  );
}
